<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="$t('attributes.payment')"
    size="large"
    @close="$emit('close')"
  >
    <template #subtitle>
      <div class="text-base text-gray-500">
        {{
          $t('attributes.outstanding_balance') +
          ' ' +
          $h.formatCurrency(getRest, invoice.currency)
        }}
      </div>
    </template>
    <template #body>
      <PaymentModalWizard
        v-model:payment="payment"
        v-model:step="step"
        v-model:cheque-duplicate="cheque_duplicate"
        :place-holder-issuer="invoice.invoiceable.full_name"
        :place-holder-direct-debit="invoice.dossier?.concernedPerson?.full_name"
        :dossier-contacts="dossier_contacts"
        :currency="invoice.currency"
        :form-submitting="formSubmitting"
        :custom-payment-modes="custom_payment_modes"
        :has-dossier="!!invoice?.dossier"
        :with-linking-step="showLinkingStep"
        @submit:payment="submitForm"
      >
        <template #linking>
          <PaymentLinkDocuments
            v-model="link_to_invoice_ids"
            :before="link_candidates.before"
            :after="link_candidates.after"
          />
        </template>
        <template #other-informations>
          <div class="col-start-1">
            <WalkmanButton
              v-if="invoice"
              v-model="payment.is_payment_deposit"
              :options="[
                { value: 1, label: $t('enums.yesNo.1') },
                { value: 0, label: $t('enums.yesNo.0') }
              ]"
              :label="$t('global.deposit_payment')"
              :edition-mode
            />
          </div>
          <div v-if="invoice.reference !== null" class="flex flex-col gap-2">
            <div>
              <h3 class="text-base font-semibold capitalize-first">
                {{ $t('attributes.invoice_status') }}
              </h3>
            </div>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div class="col-start-1">
                <SelectInvoiceStatuses
                  v-model="payment.next_invoice_status"
                  :label="$t('attributes.next_invoice_status')"
                  edition-mode
                />
              </div>
            </div>
          </div>
          <div
            v-if="
              invoice.dossier_id &&
              invoice.doc_role === 'ff' &&
              payment.next_invoice_status === 'PAI' &&
              invoice.dossier.status !== 'PAY'
            "
          >
            <div class="flex w-full flex-col">
              <h2
                class="text-xs font-medium capitalize-first"
                v-html="$t('global.change_dossier_status')"
              ></h2>
              <div class="col-start-1">
                <BaseShowEditRadio
                  v-model="next_dossier_status"
                  class="col-span-1"
                  :edition-mode="true"
                  size="extra-small"
                  name="change_status_dossier"
                  :options="[
                    { id: 1, name: $t('enums.yesNo.1') },
                    { id: 0, name: $t('enums.yesNo.0') }
                  ]"
                />
              </div>
            </div>
          </div>
        </template>
      </PaymentModalWizard>
    </template>
  </BaseCenteredModal>
  <ConfirmModal ref="paymentSyncWarning" />
</template>

<script>
import { mapActions } from 'vuex'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import PaymentModalWizard from '@ui/modal/payment/components/PaymentModalWizard.vue'
import PaymentLinkDocuments from '@ui/modal/payment/components/PaymentLinkDocuments.vue'
import {
  hasPaymentLinkCandidates,
  noPaymentLinkCandidates,
  paymentLinkCandidateLabel,
  syncedCandidatesAmong
} from '@ui/modal/payment/paymentLinkCandidates'
import { duplicateFromError } from '@ui/modal/payment/chequeNumberDuplicate'
import SelectInvoiceStatuses from '@c/InitialStateFormComponent/Enums/SelectInvoiceStatuses.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import DocTypes from '@/assets/enums/invoicing/DocTypes.js'

/** Wizard step holding the cheque number. */
const AMOUNT_STEP = 2

export default {
  name: 'PaymentInvoiceFormModal',
  components: {
    WalkmanButton,
    BaseShowEditRadio,
    SelectInvoiceStatuses,
    PaymentModalWizard,
    PaymentLinkDocuments,
    BaseCenteredModal,
    ConfirmModal
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    paymentId: {
      type: [String, Number],
      required: false,
      default: null
    },
    editBank: {
      type: [Number],
      required: false,
      default: 0
    },
    invoice: {
      type: Object,
      required: true
    },
    paymentData: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  emits: ['close', 'update:is-modal-open'],
  data() {
    return {
      step: 0,
      cheque_duplicate: null,
      formSubmitting: false,
      custom_payment_modes: [],
      dossier_contacts: [],
      originalAmount: null,
      payment: null,
      next_dossier_status: null,
      link_candidates: noPaymentLinkCandidates(),
      link_to_invoice_ids: [],
      restoringAfterConfirmation: false
    }
  },
  computed: {
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:is-modal-open', value)
      }
    },
    hasLinkCandidates() {
      return hasPaymentLinkCandidates(this.link_candidates)
    },
    /**
     * The documents to link take the room the status options need on the last
     * step, so they get a step of their own — offered on creation only, and
     * only when the document has a conversion chain to pick from.
     */
    showLinkingStep() {
      return !this.paymentId && this.hasLinkCandidates
    },
    /**
     * Editing a payment the sync wrote interrupts every invoice it settles
     * (InvoicePaymentSyncGuard::handlePaymentUpdated), not only this one.
     */
    isEditingSyncedPayment() {
      return Boolean(this.paymentId && this.payment?.chift_id)
    },
    getRest() {
      return this.invoice?.total_tvac - this.invoice?.total_paid
    },
    totalInvoiceReceived() {
      if (!this.invoice?.payments) return 0

      return this.invoice?.payments
        .filter((p) => p.payment_status === 'REC')
        .reduce((sum, p) => sum + p.amount, 0)
    }
  },
  watch: {
    isModalOpen(newValue) {
      if (newValue) {
        // Reopened by submitForm() once the sync confirmation is answered: the form was
        // restored as it was typed, a new payment would wipe it.
        if (this.restoringAfterConfirmation) {
          this.restoringAfterConfirmation = false

          return
        }

        if (!this.paymentId) {
          this.processFetchNewPaymentAction({
            invoice_id: this.invoice.id
          }).then((response) => {
            this.handleFetchApiResponse(response)
          })
          this.loadLinkCandidates()
        } else {
          this.processFetchPaymentByIdAction({
            invoice_id: this.invoice.id,
            payment_id: this.paymentId
          }).then((response) => {
            this.handleFetchApiResponse(response)
          })
        }
      } else {
        this.payment = null
        this.step = 0
        this.cheque_duplicate = null
        this.link_candidates = noPaymentLinkCandidates()
        this.link_to_invoice_ids = []
      }
    },
    'payment.amount'(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        /** For invoice with LIT status, we only allow manual status changes */
        if (
          this.invoice.status !== 'DRA' &&
          this.invoice.status !== 'LIT' &&
          this.invoice.doc_type !== DocTypes.PURCHASE_ORDER
        ) {
          let totalWithCurrent = parseFloat(this.totalInvoiceReceived)

          if (this.payment.payment_status === 'REC') {
            totalWithCurrent += parseFloat(this.payment.amount)
          }

          if (totalWithCurrent >= this.invoice.total_tvac) {
            this.payment.next_invoice_status = 'PAI'
          } else {
            this.payment.next_invoice_status = 'PP'
          }
        }
      }
    }
  },
  methods: {
    /**
     * Brings the user back to the cheque number when the save was refused
     * because another cheque already carries it. The form stays open on any
     * failure: closing it would throw away everything typed.
     */
    showChequeDuplicate(reason) {
      const duplicate = duplicateFromError(reason)

      if (duplicate) {
        this.cheque_duplicate = duplicate
        this.step = AMOUNT_STEP
      }
    },
    ...mapActions({
      processFetchInvoiceByIdAction: 'invoicing/fetchInvoiceID',
      processFetchNewPaymentAction: 'invoicing/fetchNewPayment',
      processFetchPaymentLinkCandidatesAction:
        'invoicing/fetchPaymentLinkCandidates',
      processStoreNewPaymentAction: 'invoicing/storeNewPayment',
      processFetchPaymentByIdAction: 'invoicing/fetchPaymentById',
      processUpdatePaymentByIdAction: 'invoicing/updatePaymentById'
    }),
    /**
     * The documents linked to this one (the one it was created from, the ones created from it) the
     * payment can be linked to as well. The user keeps the choice; only an unambiguous invoice
     * after the document is ticked by default. On failure the apiClient hook already announced it: no
     * second toast here, the form simply goes on without the choice.
     */
    async loadLinkCandidates() {
      const invoiceId = this.invoice.id
      // The answer may land after the modal was closed or reopened on another document: it must
      // not fill the form of a document it does not describe.
      // Not reactive on purpose: a later request (modal reopened on the same document) supersedes
      // this one, whose answer would otherwise reset what was ticked in between.
      const requestId = (this.linkCandidatesRequestId =
        (this.linkCandidatesRequestId ?? 0) + 1)
      const isStale = () =>
        !this.isModalOpen ||
        this.invoice?.id !== invoiceId ||
        requestId !== this.linkCandidatesRequestId
      let candidates = null

      // Only the request is guarded: a failure was announced by the apiClient hook and the form goes
      // on without the choice, whereas a mistake in the handling below must not be swallowed.
      try {
        candidates = await this.processFetchPaymentLinkCandidatesAction({
          invoice_id: invoiceId
        })
      } catch (_error) {
        candidates = null
      }

      if (isStale()) return

      this.link_candidates = candidates ?? noPaymentLinkCandidates()
      this.link_to_invoice_ids = [...(candidates?.preselected_ids ?? [])]
    },
    /**
     * What the confirmation says is interrupted: the sync of this invoice, and the one of each
     * ticked document whose payments are synced from the accounting.
     */
    syncInterruptionMessage(syncedLinkedDocuments) {
      const messages = []

      // A synced payment is shared by every invoice it settles: editing it
      // interrupts each of them, whatever the state of this one.
      if (this.isEditingSyncedPayment) {
        messages.push(
          this.$t('invoicing.payments_sync_interrupt_linked_documents')
        )
      } else if (this.invoice?.payments_sync_status === 'ENA') {
        messages.push(this.$t('invoicing.payments_sync_interrupt_warning'))
      }

      if (syncedLinkedDocuments.length > 0) {
        messages.push(
          this.$t('invoicing.payments_sync_interrupt_linked_warning', {
            documents: syncedLinkedDocuments
              .map((document) =>
                paymentLinkCandidateLabel((key) => this.$t(key), document)
              )
              .join(', ')
          })
        )
      }

      return messages.join(' ')
    },
    handleFetchApiResponse(response) {
      this.payment = {
        ...response.payment,
        ...this.paymentData,
        payment_status:
          this.paymentData?.payment_status ?? response.payment.payment_status,
        payment_type: 'CRE',
        next_invoice_status: this.invoice.status
      }
      this.originalAmount = this.payment.amount
      this.custom_payment_modes = response.metadata.payment_modes ?? []
      this.dossier_contacts = response.metadata.dossier_contacts ?? []
      if (
        (this.$can('super-admin') || this.$can('manager')) &&
        this.custom_payment_modes.length > 0
      ) {
        this.custom_payment_modes.push({
          id: 'custom',
          denomination: {
            [this.$i18n.locale]: this.$t('global.predefined')?.replace(
              /^./,
              (c) => c.toUpperCase()
            )
          }
        })
        this.payment.payment_mode_id = this.payment.payment_mode_id ?? 'custom'
      }
      if (this.custom_payment_modes.length === 0) {
        this.payment.payment_mode_id = 'custom'
      }
    },
    async submitForm() {
      if (this.amountErrors?.length) return
      if (this.payment_modes?.length > 0 && !this.payment.payment_mode_id) {
        this.payment_mode_error = true
        return
      }

      const syncedLinkedDocuments = syncedCandidatesAmong(
        this.link_candidates,
        this.link_to_invoice_ids
      )

      if (
        this.invoice?.payments_sync_status === 'ENA' ||
        this.isEditingSyncedPayment ||
        syncedLinkedDocuments.length > 0
      ) {
        // Held across the await on purpose: the confirmation is a sibling dialog, and
        // opening it makes the form modal report itself closed, which nulls
        // this.payment through the isModalOpen watcher. Without this snapshot the
        // submission below would post an empty payment.
        const pendingPayment = { ...this.payment }
        const pendingDossierStatus = this.next_dossier_status
        const pendingStep = this.step
        const pendingLinkCandidates = this.link_candidates
        const pendingLinkIds = this.link_to_invoice_ids

        const confirmed = await this.$refs.paymentSyncWarning.show({
          title: this.$t('invoicing.payments_sync_interrupt_title'),
          message: this.syncInterruptionMessage(syncedLinkedDocuments),
          okButton: this.$t('global.yes')
        })

        // Restored on both paths: the state was wiped when the confirmation opened,
        // so refusing the interruption has to put the form back too. Returning
        // without restoring would close the whole form and lose what was typed —
        // the outcome of the most cautious click available.
        this.payment = pendingPayment
        this.next_dossier_status = pendingDossierStatus
        this.step = pendingStep
        this.link_candidates = pendingLinkCandidates
        this.link_to_invoice_ids = pendingLinkIds
        // The confirmation made the form report itself closed: reopening it must not
        // start a new payment (see the isModalOpen watcher).
        this.restoringAfterConfirmation = !this.isModalOpen
        this.localIsModalOpen = true
        // The parent has followed by then: a flag its watcher never consumed (the
        // parent ignoring the update) must not skip the next real opening.
        await this.$nextTick()
        this.restoringAfterConfirmation = false

        if (!confirmed) {
          return
        }
      }

      this.formSubmitting = true
      if (!this.paymentId) {
        this.processStoreNewPaymentAction({
          invoice_id: this.invoice.id,
          payment: {
            ...this.payment,
            link_to_invoice_ids: this.link_to_invoice_ids
          },
          change_status_dossier: this.next_dossier_status
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_added_to_invoice'))
            this.localIsModalOpen = false
            this.formSubmitting = false
            this.next_dossier_status = null
            this.bankType = 1
            this.payment_mode_error = false
          })
          .catch((reason) => {
            this.formSubmitting = false
            this.showChequeDuplicate(reason)
          })
      } else {
        this.processUpdatePaymentByIdAction({
          invoice_id: this.invoice.id,
          payment_id: this.payment.id,
          payment: this.payment,
          change_status_dossier: this.change_status_dossier
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_updated'))
            this.localIsModalOpen = false
            this.formSubmitting = false
            this.change_status_dossier = null
            this.payment_mode_error = false
          })
          .catch((reason) => {
            this.formSubmitting = false
            this.showChequeDuplicate(reason)
          })
      }
    }
  }
}
</script>
